import React from 'react';
import { format, isToday, isTomorrow, isYesterday, differenceInDays } from 'date-fns';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';
import { TaskPriority, TaskStatus } from '@/types/lead';
import { getTaskPriorityOptions } from '@/services/leads/taskService';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

export const useTaskDisplay = () => {
  const { data: companyMembers } = useCompanyMembers();
  const priorityOptions = getTaskPriorityOptions();

  const renderAssignee = (assignedTo?: string): React.ReactNode => {
    if (!assignedTo || !companyMembers) return null;
    
    const member = companyMembers.find(m => m.id === assignedTo);
    if (!member) return null;
    
    return (
      <div className="flex items-center space-x-2">
        <Avatar className="h-6 w-6">
          <AvatarImage src={member.avatar_url || ''} />
          <AvatarFallback className="text-xs">
            {member.first_name?.[0]}{member.last_name?.[0]}
          </AvatarFallback>
        </Avatar>
        <span className="text-xs text-muted-foreground">
          Assigned to {member.first_name} {member.last_name}
        </span>
      </div>
    );
  };

  const formatDueDate = (dueDate: Date | string | undefined): string => {
    if (!dueDate) return "No due date";
    
    const date = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
    
    if (isToday(date)) {
      return `Today at ${format(date, 'h:mm a')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow at ${format(date, 'h:mm a')}`;
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  const getPriorityBadge = (priority: TaskPriority): React.ReactNode => {
    const option = priorityOptions.find(opt => opt.value === priority) || priorityOptions[0];
    return (
      <Badge className={`${option.color} text-white`}>
        {option.label}
      </Badge>
    );
  };

  const getTaskStatusIndicator = (status: TaskStatus, dueDate?: Date | string): React.ReactNode => {
    if (status === 'completed' as TaskStatus) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
        </Badge>
      );
    }
    
    if (!dueDate) return null;
    
    const dueDateObj = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
    const isOverdue = dueDateObj < new Date() && status !== 'completed';
    
    if (isOverdue) {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
          <AlertTriangle className="h-3 w-3 mr-1" /> Overdue
        </Badge>
      );
    }
    
    const today = new Date();
    const daysUntilDue = differenceInDays(dueDateObj, today);
    
    if (daysUntilDue <= 1 && daysUntilDue >= 0) {
      return (
        <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200">
          <Clock className="h-3 w-3 mr-1" /> Due Soon
        </Badge>
      );
    }
    
    return null;
  };

  return {
    renderAssignee,
    formatDueDate,
    getPriorityBadge,
    getTaskStatusIndicator,
  };
};
